import { useUser } from '@folklore/auth';
import { TrackingContainer } from '@folklore/tracking';
import { ReactNode, useEffect, useMemo } from 'react';

import { useAddAudiences } from '../hooks/useVisitor';
import Tracking from '../lib/Tracking';

import { useIsCheckingAuth } from './AuthContext';
import { useConsentGiven } from './ConsentContext';
import { useSite } from './SiteContext';
import { useVisitorAudiences, useVisitorId } from './VisitorContext';
import createDebug from 'debug';

const debug = createDebug('app:tracking');

interface TrackingProviderProps {
    children: ReactNode;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    variables?: Record<string, any> | null;
}

export function TrackingProvider({ children, variables = null }: TrackingProviderProps) {
    const user = useUser();
    // const hasConsent = useConsent('analytics_storage');
    const visitorId = useVisitorId();
    const visitorAudiences = useVisitorAudiences();
    // const consentNeeded = useConsentNeeded();
    const consentGiven = useConsentGiven();
    const isCheckingAuth = useIsCheckingAuth();
    const { audiences = [] } = useSite();
    const paused = isCheckingAuth;
    const { addAudiences } = useAddAudiences();
    const tracking = useMemo(
        () =>
            new Tracking({
                user,
                paused,
            }),
        [],
    );

    useEffect(() => {
        if (variables !== null) {
            tracking.setVariables(variables);
        }
    }, [variables]);

    useEffect(() => {
        if (!isCheckingAuth) {
            debug('setting user %O', user);
            tracking.setVariables({
                visitorId,
                audiences: visitorAudiences,
            });
            tracking.setUser(user);
        }
    }, [user, isCheckingAuth]);

    useEffect(() => {
        tracking.setVariables({
            consentGiven,
        });
    }, [consentGiven]);

    useEffect(() => {
        if (visitorId !== null || visitorAudiences !== null) {
            debug('setting visitor: %s and audiences: %O', visitorId, audiences);
            tracking.setVariables({
                visitorId,
                audiences: visitorAudiences,
            });
        }
    }, [visitorId, visitorAudiences]);

    useEffect(() => {
        debug(paused ? 'Pause' : 'Resume');
        tracking.setPaused(paused);
    }, [paused]);

    useEffect(() => {
        function onAudienceTrigger({ handle }: { handle: string }) {
            debug('Add audience: %s', handle);
            addAudiences(handle);
        }

        const audiencesWithTrigger =
            audiences !== null ? audiences.filter(({ trigger = null }) => trigger !== null) : [];
        audiencesWithTrigger.forEach((audience) => {
            const { handle, trigger } = audience;
            tracking.triggers.register(`audience_${handle}`, trigger, () =>
                onAudienceTrigger(audience),
            );
        });

        return () => {
            audiencesWithTrigger.forEach(({ handle }) => {
                tracking.triggers.unregister(`audience_${handle}`);
            });
        };
    }, [tracking, audiences, addAudiences]);

    useEffect(() => {
        function onInAppEvent(event: { type: string; details?: Record<string, string> }) {
            tracking.push({
                event: event.type,
                ...(event.detail || {}),
            });
        }
        let setuped = false;
        function setupInAppEvents() {
            debug('Set up customer.io in-app events');
            window.cioanalytics.on('in-app', onInAppEvent);
            setuped = true;
        }

        if (
            typeof window.cioanalytics !== 'undefined' &&
            typeof window.cioanalytics.on !== 'undefined'
        ) {
            setupInAppEvents();
        } else {
            window.cioanalyticsReady = function () {
                setupInAppEvents();
            };
        }

        return () => {
            if (setuped) {
                window.cioanalytics.off('in-app', onInAppEvent);
            }
        };
    }, [tracking]);

    return <TrackingContainer tracking={tracking}>{children}</TrackingContainer>;
}

export default TrackingProvider;
